import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, forkJoin, map, Observable, of } from "rxjs";
import { ObjectUtil } from "../../../../components/common/object-util/object-util";
import { AppServiceHelperService } from "../../../../services/app-service-helper.service";
import { ReportDesignerPage } from "../../models/report-designer-page";
import { reportDesignerWigdets } from "../../widgets/widgetsRegistry";
import { ReportDesignerStorageService } from "../report-designer-storage.service";

/**
 * Report Data Preloader Service
 */
@Injectable({
  providedIn: 'root'
})
export class ReportDataPreloaderService {
  /**
   * Widget data
   */
  private widgetsData = new Map<string, any>;

  /**
   * Constructor
   * @param rdStorage ReportDesignerStorageService
   * @param appServiceHelper AppServiceHelperService
   * @param httpClient HttpClient
   */
  constructor(private rdStorage: ReportDesignerStorageService,
    private appServiceHelper: AppServiceHelperService,
    private httpClient: HttpClient) {
  }

  /**
   * Gets data from a URL
   * @param url
   * @returns
   */
  getDataForUrl(url: string) {
    const key = url?.toLowerCase();
    return this.widgetsData.has(key)
      ? ObjectUtil.deepCopy(this.widgetsData.get(key)) //return cloned data in case widget alters data
      : undefined;
  }

  /**
   * Preloads data
   * @param pages
   * @returns
   */
  preloadData(pages: ReportDesignerPage[]): Promise<void> {
    return new Promise((resolve) => {
      const reportId = this.rdStorage.getReport();
      if (!reportId) {
        //no report => resolve immediatelly
        resolve();
        return;
      }

      const flattenArrays = arr => arr.reduce((a, e) => [...a, ...e], []);

      //use Set to get distinct widgets
      const widgets = new Set(
        flattenArrays(
          pages.map(page => page.widgets.map(widget => widget.template.substring(1, widget.template.indexOf(' ')).split('-').join('').toLowerCase()))
        )
      );

      const widgetComponents = reportDesignerWigdets
        .filter(widget => widgets.has(widget.downgradedName.toLowerCase()))
        .map((widget) => (widget.componentClass));

      const widgetResource = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets');

      const widgetUrlActions = Array.from(new Set(
        widgetComponents.map(wc => wc.prototype.constructor.name.replace('WidgetComponent', '').toLowerCase())
          .filter(action =>
            //remove some "don't load data" widgets
            action !== 'fullline'
            && action !== 'mainsectiontitle'
            && action !== 'appendixpage'
            //and some with more complicated urls
            //TODO: handle this too somehow
            && action !== 'text'
            && action !== 'ownershipandtransactionhistory'
            && action !== 'calculationofthepotentialexitvalue'
            && action !== 'valuationresultsresidual'
            && action !== 'capitalexpenditurecalculationofdevelopmentcosts'
            && action !== 'picture'
          )
          .map(action => {
            //adjust some specific widgets
            switch (action) {
              case 'signature':
              case 'license':
                return 'signatureAndLicense';
              default:
                return action;
            }
          })
          .filter(action => action !== '')
          .map(action => action.toLowerCase())
      ));

      const urls = widgetUrlActions.map(action => `${widgetResource}/${action}?id=${reportId}`);

      if (urls.length > 0) {
        const observables = urls.map(url => {
          return this.httpClient.get(url)
            .pipe(map((res) => res),
              catchError(() => of(undefined)));//ignore errors, data will be loaded when needed by widget itself
        });

        this.loadData(observables, urls, resolve);
      } else {
        //nothing to load
        resolve();
      }
    });
  }

  /**
   * Loads data
   * @param observables
   * @param urls
   * @param resolve
   */
  private loadData(observables: Observable<unknown>[], urls: string[], resolve: (value: void | PromiseLike<void>) => void) {
    forkJoin(observables).subscribe({
      next: (responses) => {
        for (let idx = 0; idx < responses.length; idx++) {
          const url = urls[idx];
          const response = responses[idx];
          if (response) {
            this.widgetsData.set(url.toLowerCase(), response);
          }
        }

        resolve();
      },
      error: () => {
        // error occurred => ignore
        resolve();
      }
    });
  }
}
